import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectValue, SelectTrigger as BaseSelectTrigger } from "../ui/select";
import { color as FKc, type as Tk } from "../product_config/formkit";
import {
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  CheckCircle,
  Shield,
  Heart,
  Star,
  Award,
  ThumbsUp,
  Zap,
  Gift,
  Users,
  Clock,
  Phone,
  Mail,
  MapPin,
  Home,
  Car,
  Plane,
  Umbrella,
  ShieldCheck,
  PiggyBank
} from "lucide-react";
import { toast } from "sonner";
import { updatePlanConfiguration } from "../../services/supabase/planesSupabaseService";
// Importar tokens centralizados del sistema de diseño
import {
  colors,
  spacing,
  typography,
  shadows,
  borders,
  cardTokens,
  buttonTokens
} from "../../styles/tokens/index.js";

/* Shadows de primitivos con el look del design system.
   Reemplazan Input / Textarea / Label / Field / Card / SelectTrigger conservando
   la misma API (+ forward de className y style) para que el cuerpo (layouts
   Tailwind, logica y helpers) quede intacto y solo cambie la capa visual. */
const _dsField = (focused, extra) => ({
  width: "100%", boxSizing: "border-box", height: 40, padding: "0 12px", borderRadius: 8,
  border: "1.5px solid " + (focused ? FKc.cta : FKc.border), background: FKc.field, color: FKc.text,
  fontSize: 14, fontFamily: "inherit", outline: "none", transition: "border-color .15s ease", ...extra,
});
function Input({ className, style, onChange, value, onFocus, onBlur, ...rest }) {
  const [f, setF] = useState(false);
  return (
    <input
      {...rest} value={value ?? ""} onChange={onChange} className={className}
      onFocus={(e) => { setF(true); onFocus && onFocus(e); }}
      onBlur={(e) => { setF(false); onBlur && onBlur(e); }}
      style={{ ..._dsField(f), ...style }}
    />
  );
}
function Textarea({ className, style, onChange, value, rows = 3, onFocus, onBlur, ...rest }) {
  const [f, setF] = useState(false);
  return (
    <textarea
      {...rest} rows={rows} value={value ?? ""} onChange={onChange} className={className}
      onFocus={(e) => { setF(true); onFocus && onFocus(e); }}
      onBlur={(e) => { setF(false); onBlur && onBlur(e); }}
      style={{ ..._dsField(f, { height: "auto", padding: "10px 12px", lineHeight: 1.5, resize: "vertical", minHeight: 40 }), ...style }}
    />
  );
}
function Label({ className, style, required, children, ...rest }) {
  return (
    <label {...rest} className={className} style={{ display: "block", ...Tk.label, ...style }}>
      {children}{required && <span style={{ color: FKc.errBorder, marginLeft: 3 }}>*</span>}
    </label>
  );
}
const FieldLabel = Label;
function FieldDescription({ className, style, children, ...rest }) {
  return <p {...rest} className={className} style={{ margin: "2px 0 0", ...Tk.hint, ...style }}>{children}</p>;
}
function Field({ className, style, children, ...rest }) {
  return <div {...rest} className={className} style={{ display: "flex", flexDirection: "column", gap: 7, ...style }}>{children}</div>;
}
function FieldGroup({ className, style, children, ...rest }) {
  return <div {...rest} className={className} style={{ display: "flex", flexDirection: "column", gap: 18, ...style }}>{children}</div>;
}
function Card({ className, style, children, ...rest }) {
  return <div {...rest} className={className} style={{ background: FKc.surface, border: "1px solid " + FKc.border, borderRadius: 16, ...style }}>{children}</div>;
}
function CardHeader({ className, style, children, ...rest }) {
  return <div {...rest} className={className} style={{ padding: "20px 24px 8px", ...style }}>{children}</div>;
}
function CardTitle({ className, style, children, ...rest }) {
  return <div {...rest} className={className} style={{ ...Tk.sectionTitle, ...style }}>{children}</div>;
}
function CardContent({ className, style, children, ...rest }) {
  return <div {...rest} className={className} style={{ padding: "12px 24px 20px", ...style }}>{children}</div>;
}
function SelectTrigger({ className, style, children, ...rest }) {
  return (
    <BaseSelectTrigger {...rest} className={className} style={{ height: 40, minHeight: 40, border: "1.5px solid " + FKc.border, borderRadius: 8, background: FKc.field, ...style }}>
      {children}
    </BaseSelectTrigger>
  );
}
// Mapeo de iconos con componentes de Lucide React (definido globalmente)
const iconComponents = {
  "check-circle": CheckCircle,
  "shield": Shield,
  "heart": Heart,
  "star": Star,
  "award": Award,
  "thumbs-up": ThumbsUp,
  "zap": Zap,
  "gift": Gift,
  "users": Users,
  "clock": Clock,
  "phone": Phone,
  "mail": Mail,
  "map-pin": MapPin,
  "home": Home,
  "car": Car,
  "plane": Plane,
  "umbrella": Umbrella,
  "shield-check": ShieldCheck,
  "piggy-bank": PiggyBank,
};

const FANCY_YOOL = {
  teal: { hex: "#007D6E", className: "fancyyool-highlight-teal" },
  cyan: { hex: "#06B6D4", className: "fancyyool-highlight-cyan" },
  yellow: { hex: "#F5C400", className: "fancyyool-highlight-orange" },
  violet: { hex: "#5B2BE5", className: "fancyyool-highlight-violet" },
  indigo: { hex: "#7D7CFF", className: "fancyyool-highlight-indigo" },
  blue: { hex: "#2563EB", className: "fancyyool-highlight-blue" },
  orange: { hex: "#D35400", className: "fancyyool-highlight-orange" },
  purple: { hex: "#A855F7", className: "fancyyool-highlight-purple" },
  pink: { hex: "#E91E63", className: "fancyyool-highlight-pink" },
  sky: { hex: "#0EA5E9", className: "fancyyool-highlight-sky" },
  lime: { hex: "#147D89", className: "fancyyool-highlight-lime" },
  red: { hex: "#EF4444", className: "fancyyool-highlight-red" },
  slate: { hex: "#64748B", className: "fancyyool-highlight-slate" },
  gray: { hex: "#6B7280", className: "fancyyool-highlight-gray" },
};

const fancyColorOptions = Object.entries(FANCY_YOOL).map(([value, config]) => ({
  value,
  hex: config.hex,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}));

const getFancyHighlightClass = (color) => FANCY_YOOL[color]?.className || FANCY_YOOL.teal.className;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const renderHighlightedText = (text, highlightedWords) => {
  if (!text) return null;

  const validHighlightedWords = (highlightedWords || [])
    .filter((item) => item?.texto?.trim())
    .sort((a, b) => b.texto.trim().length - a.texto.trim().length);

  if (!validHighlightedWords.length) {
    return text;
  }

  const pattern = validHighlightedWords
    .map((item) => escapeRegExp(item.texto.trim()))
    .join("|");

  if (!pattern) {
    return text;
  }

  const segments = text.split(new RegExp(`(${pattern})`, "gi"));

  return segments.map((segment, index) => {
    const matchedWord = validHighlightedWords.find(
      (item) => item.texto.trim().toLowerCase() === segment.toLowerCase()
    );

    if (!matchedWord) {
      return <React.Fragment key={`${segment}-${index}`}>{segment}</React.Fragment>;
    }

    return (
      <span key={`${segment}-${index}`} className={getFancyHighlightClass(matchedWord.color)}>
        {segment}
      </span>
    );
  });
};

const normalizeJsonValue = (value) => {
  if (value == null || value === "") return null;

  let current = value;

  for (let i = 0; i < 3; i += 1) {
    if (typeof current !== "string") return current;

    const trimmed = current.trim();
    if (!trimmed) return null;

    try {
      current = JSON.parse(trimmed);
    } catch {
      return current;
    }
  }

  return current;
};

export default function ConfiguracionPlanTab({ 
  formData, 
  handleChange,
  planId,
  recordId,
  onSave
}) {
  const [jsonArrayData, setJsonArrayData] = useState({
    nota: "",
    uuid: "",
    fkFam: "",
    fkUrl: "",
    items: [],
    nombre: "",
    status: "active",
    archived: 0,
    urlImagen: "",
    descripcion: "",
    landbotConfig: "",
    badgeDestacador: "",
    urlContratacion: "",
    comunicacion: {
      titulo: "",
      contenido: "",
      palabrasDestacadas: [],
    }
  });

  const [jsonMailsData, setJsonMailsData] = useState(() => ({
    email_cotizacion: {
      cta: { url: "", texto: "" },
      header: { titulo: "", subtitulo: "" },
      saludo: { texto: "" },
      beneficios: { titulo: "", items: [] }
    },
    email_contratacion: {
      cta: { url: "", titulo: "", subtitulo: "", texto_boton: "" },
      header: { titulo: "", subtitulo: "" },
      saludo: { titulo: "", texto: "" },
      info_cards: []
    }
  }));

  const [iconos_disponibles] = useState(() => ({
    "check-circle": "Círculo con check",
    "shield": "Escudo",
    "heart": "Corazón",
    "star": "Estrella",
    "award": "Premio",
    "thumbs-up": "Pulgar arriba",
    "zap": "Rayo",
    "gift": "Regalo",
    "users": "Usuarios",
    "clock": "Reloj",
    "phone": "Teléfono",
    "mail": "Correo",
    "map-pin": "Ubicación",
    "home": "Casa",
    "car": "Auto",
    "plane": "Avión",
    "umbrella": "Paraguas",
    "shield-check": "Escudo con check",
    "piggy-bank": "Alcancía"
  }));

  const [isSaving, setIsSaving] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    item: "",
    tipo: "",
    titulo: ""
  });
  const [newHighlightedWord, setNewHighlightedWord] = useState({
    texto: "",
    color: "teal",
  });

  const tiposDisponibles = [
    "titulo",
    "queEs",
    "queNoCubre",
    "beneficio",
    "cobertura",
    "vigencia"
  ];

  // Cargar datos existentes al montar el componente
  useEffect(() => {
    if (formData.jsonarray) {
      try {
        const parsedArray = normalizeJsonValue(formData.jsonarray);
        if (parsedArray && typeof parsedArray === "object") {
          setJsonArrayData((prev) => ({
            ...prev,
            ...parsedArray,
            items: Array.isArray(parsedArray.items) ? parsedArray.items : [],
            comunicacion: {
              ...prev.comunicacion,
              ...(parsedArray.comunicacion && typeof parsedArray.comunicacion === "object" ? parsedArray.comunicacion : {}),
              palabrasDestacadas: Array.isArray(parsedArray?.comunicacion?.palabrasDestacadas)
                ? parsedArray.comunicacion.palabrasDestacadas
                : [],
            },
          }));
        }
      } catch (error) {
        console.error("Error parsing jsonarray:", error);
      }
    }

    if (formData.jsonmails) {
      try {
        const parsedMails = normalizeJsonValue(formData.jsonmails);

        const defaults = {
          email_cotizacion: {
            cta: { url: "", texto: "" },
            header: { titulo: "", subtitulo: "" },
            saludo: { texto: "" },
            beneficios: { titulo: "", items: [] }
          },
          email_contratacion: {
            cta: { url: "", titulo: "", subtitulo: "", texto_boton: "" },
            header: { titulo: "", subtitulo: "" },
            saludo: { titulo: "", texto: "" },
            info_cards: []
          }
        };

        if (parsedMails && typeof parsedMails === "object") {
          const safeMails = {
            email_cotizacion: {
              ...defaults.email_cotizacion,
              ...(parsedMails?.email_cotizacion || {}),
              cta: {
                ...defaults.email_cotizacion.cta,
                ...(parsedMails?.email_cotizacion?.cta || {})
              },
              header: {
                ...defaults.email_cotizacion.header,
                ...(parsedMails?.email_cotizacion?.header || {})
              },
              saludo: {
                ...defaults.email_cotizacion.saludo,
                ...(parsedMails?.email_cotizacion?.saludo || {})
              },
              beneficios: {
                ...defaults.email_cotizacion.beneficios,
                ...(parsedMails?.email_cotizacion?.beneficios || {}),
                items: Array.isArray(parsedMails?.email_cotizacion?.beneficios?.items)
                  ? parsedMails.email_cotizacion.beneficios.items
                  : []
              }
            },
            email_contratacion: {
              ...defaults.email_contratacion,
              ...(parsedMails?.email_contratacion || {}),
              cta: {
                ...defaults.email_contratacion.cta,
                ...(parsedMails?.email_contratacion?.cta || {})
              },
              header: {
                ...defaults.email_contratacion.header,
                ...(parsedMails?.email_contratacion?.header || {})
              },
              saludo: {
                ...defaults.email_contratacion.saludo,
                ...(parsedMails?.email_contratacion?.saludo || {})
              },
              info_cards: Array.isArray(parsedMails?.email_contratacion?.info_cards)
                ? parsedMails.email_contratacion.info_cards
                : []
            }
          };

          setJsonMailsData(safeMails);
        }
      } catch (error) {
        console.error("Error parsing jsonmails:", error);
      }
    }
  }, [formData.jsonarray, formData.jsonmails]);

  // Funciones para manejar jsonarray
  const handleArrayFieldChange = (field, value) => {
    const updatedData = {
      ...jsonArrayData,
      [field]: value
    };
    setJsonArrayData(updatedData);
    
    // Actualizar formData inmediatamente
    handleChange({
      target: {
        name: 'jsonarray',
        value: JSON.stringify(updatedData)
      }
    });
  };

  const handleAddItem = () => {
    if (!newItem.item || !newItem.tipo || !newItem.titulo) {
      toast.error("Todos los campos son requeridos");
      return;
    }

    const updatedData = {
      ...jsonArrayData,
      items: [...jsonArrayData.items, { ...newItem }]
    };
    
    setJsonArrayData(updatedData);
    
    // Actualizar formData inmediatamente
    handleChange({
      target: {
        name: 'jsonarray',
        value: JSON.stringify(updatedData)
      }
    });

    setNewItem({ item: "", tipo: "", titulo: "" });
    toast.success("Item agregado correctamente");
  };

  const handleCommunicationChange = (field, value) => {
    const updatedData = {
      ...jsonArrayData,
      comunicacion: {
        ...(jsonArrayData.comunicacion || {}),
        [field]: value,
      },
    };

    setJsonArrayData(updatedData);
    handleChange({
      target: {
        name: "jsonarray",
        value: JSON.stringify(updatedData),
      }
    });
  };

  const handleAddHighlightedWord = () => {
    if (!newHighlightedWord.texto.trim()) {
      toast.error("La palabra destacada es requerida");
      return;
    }

    const updatedData = {
      ...jsonArrayData,
      comunicacion: {
        ...(jsonArrayData.comunicacion || {}),
        palabrasDestacadas: [
          ...((jsonArrayData.comunicacion?.palabrasDestacadas || []).filter(Boolean)),
          {
            texto: newHighlightedWord.texto.trim(),
            color: newHighlightedWord.color || "teal",
          },
        ],
      },
    };

    setJsonArrayData(updatedData);
    handleChange({
      target: {
        name: "jsonarray",
        value: JSON.stringify(updatedData),
      }
    });
    setNewHighlightedWord({
      texto: "",
      color: "teal",
    });
  };

  const handleHighlightedWordChange = (index, field, value) => {
    const updatedWords = (jsonArrayData.comunicacion?.palabrasDestacadas || []).map((item, itemIndex) =>
      itemIndex === index
        ? { ...item, [field]: value }
        : item
    );

    const updatedData = {
      ...jsonArrayData,
      comunicacion: {
        ...(jsonArrayData.comunicacion || {}),
        palabrasDestacadas: updatedWords,
      },
    };

    setJsonArrayData(updatedData);
    handleChange({
      target: {
        name: "jsonarray",
        value: JSON.stringify(updatedData),
      }
    });
  };

  const handleRemoveHighlightedWord = (index) => {
    const updatedData = {
      ...jsonArrayData,
      comunicacion: {
        ...(jsonArrayData.comunicacion || {}),
        palabrasDestacadas: (jsonArrayData.comunicacion?.palabrasDestacadas || []).filter((_, itemIndex) => itemIndex !== index),
      },
    };

    setJsonArrayData(updatedData);
    handleChange({
      target: {
        name: "jsonarray",
        value: JSON.stringify(updatedData),
      }
    });
  };

  const handleEditItem = (index) => {
    setEditingItem(index);
  };

  const handleSaveItem = (index, updatedItem) => {
    const updatedData = {
      ...jsonArrayData,
      items: jsonArrayData.items.map((item, i) => i === index ? updatedItem : item)
    };
    
    setJsonArrayData(updatedData);
    
    // Actualizar formData inmediatamente
    handleChange({
      target: {
        name: 'jsonarray',
        value: JSON.stringify(updatedData)
      }
    });
    
    setEditingItem(null);
    toast.success("Item actualizado correctamente");
  };

  const handleDeleteItem = (index) => {
    const updatedData = {
      ...jsonArrayData,
      items: jsonArrayData.items.filter((_, i) => i !== index)
    };
    
    setJsonArrayData(updatedData);
    
    // Actualizar formData inmediatamente
    handleChange({
      target: {
        name: 'jsonarray',
        value: JSON.stringify(updatedData)
      }
    });
    
    toast.success("Item eliminado correctamente");
  };

  // Función para guardar cambios en la base de datos usando Supabase
  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Preparar datos para actualizar en Supabase
      const configData = {
        jsonarray: JSON.stringify(jsonArrayData),
        jsonmails: JSON.stringify(jsonMailsData)
      };
      
      // Usar planId o recordId según esté disponible
      const id = planId || recordId;
      if (!id) {
        toast.error("No se encontró el ID del plan para guardar");
        return;
      }
      
      // Llamar al servicio de Supabase para actualizar configuración
      await updatePlanConfiguration(id, configData);
      
      toast.success("Configuración guardada correctamente en Supabase");
      if (onSave) onSave();
    } catch (error) {
      console.error("Error guardando configuración:", error);
      toast.error("Error al guardar la configuración");
    } finally {
      setIsSaving(false);
    }
  };

  // Funciones para manejar jsonmails
  const handleMailFieldChange = (section, field, value) => {
    const updatedData = {
      ...jsonMailsData,
      [section]: {
        ...jsonMailsData[section],
        [field]: value
      }
    };
    
    setJsonMailsData(updatedData);
    
    // Actualizar formData inmediatamente
    handleChange({
      target: {
        name: 'jsonmails',
        value: JSON.stringify(updatedData)
      }
    });
  };

  const handleNestedMailFieldChange = (section, parentField, field, value) => {
    const updatedData = {
      ...jsonMailsData,
      [section]: {
        ...jsonMailsData[section],
        [parentField]: {
          ...(jsonMailsData[section]?.[parentField] || {}),
          [field]: value
        }
      }
    };
    
    setJsonMailsData(updatedData);
    
    // Actualizar formData inmediatamente
    handleChange({
      target: {
        name: 'jsonmails',
        value: JSON.stringify(updatedData)
      }
    });
  };

  const handleAddBeneficio = (section) => {
    const newBeneficio = {
      icon: "circle-check",
      titulo: "",
      content: ""
    };

    if (section === 'email_cotizacion') {
      const updatedData = {
        ...jsonMailsData,
        email_cotizacion: {
          ...jsonMailsData.email_cotizacion,
          beneficios: {
            ...jsonMailsData.email_cotizacion.beneficios,
            items: [...(jsonMailsData.email_cotizacion.beneficios.items || []), newBeneficio]
          }
        }
      };
      
      setJsonMailsData(updatedData);
      
      // Actualizar formData inmediatamente
      handleChange({
        target: {
          name: 'jsonmails',
          value: JSON.stringify(updatedData)
        }
      });
    }
  };

  const handleAddInfoCard = () => {
    const newCard = {
      icon: "lightning",
      titulo: "",
      content: ""
    };

    const updatedData = {
      ...jsonMailsData,
      email_contratacion: {
        ...jsonMailsData.email_contratacion,
        info_cards: [...(jsonMailsData.email_contratacion.info_cards || []), newCard]
      }
    };
    
    setJsonMailsData(updatedData);
    
    // Actualizar formData inmediatamente
    handleChange({
      target: {
        name: 'jsonmails',
        value: JSON.stringify(updatedData)
      }
    });
  };

  const handleAddBeneficioResumen = () => {
    const newBeneficio = {
      icon: "circle-dollar",
      bajada: "",
      titulo: ""
    };

    const updatedData = {
      ...jsonMailsData,
      email_contratacion: {
        ...jsonMailsData.email_contratacion,
        beneficios_resumen: {
          ...jsonMailsData.email_contratacion.beneficios_resumen,
          cards_grid: [...(jsonMailsData.email_contratacion.beneficios_resumen.cards_grid || []), newBeneficio]
        }
      }
    };
    
    setJsonMailsData(updatedData);
    
    // Actualizar formData inmediatamente
    handleChange({
      target: {
        name: 'jsonmails',
        value: JSON.stringify(updatedData)
      }
    });
  };

  // Agrupar items por tipo
  const groupedItems = (jsonArrayData.items || []).reduce((acc, item, index) => {
    if (!acc[item.tipo]) {
      acc[item.tipo] = [];
    }
    acc[item.tipo].push({ ...item, originalIndex: index });
    return acc;
  }, {});

  const communicationHighlightedWords = (jsonArrayData.comunicacion?.palabrasDestacadas || []).filter(
    (item) => item?.texto?.trim()
  );

  return (
    <div className="space-y-6">
      {/* Sección JSONArray */}
      <Card>
        <CardHeader>
          <CardTitle>Configuración JSONArray</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Campos básicos */}
          <FieldGroup>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field>
                <FieldLabel required>Nombre</FieldLabel>
                <Input
                  value={jsonArrayData.nombre}
                  onChange={(e) => handleArrayFieldChange('nombre', e.target.value)}
                  placeholder="Nombre del plan"
                />
                <FieldDescription>
                  Nombre identificativo del plan de seguros
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel>UUID</FieldLabel>
                <Input
                  value={jsonArrayData.uuid}
                  onChange={(e) => handleArrayFieldChange('uuid', e.target.value)}
                  placeholder="UUID único"
                />
                <FieldDescription>
                  Identificador único universal del plan
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel>URL Imagen</FieldLabel>
                <Input
                  value={jsonArrayData.urlImagen}
                  onChange={(e) => handleArrayFieldChange('urlImagen', e.target.value)}
                  placeholder="URL de la imagen"
                />
                <FieldDescription>
                  URL de la imagen representativa del plan
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel>URL Contratación</FieldLabel>
                <Input
                  value={jsonArrayData.urlContratacion}
                  onChange={(e) => handleArrayFieldChange('urlContratacion', e.target.value)}
                  placeholder="URL de contratación"
                />
                <FieldDescription>
                  Enlace directo para contratar el plan
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel>Badge Destacador</FieldLabel>
                <Input
                  value={jsonArrayData.badgeDestacador}
                  onChange={(e) => handleArrayFieldChange('badgeDestacador', e.target.value)}
                  placeholder="ej: alta cobertura"
                />
                <FieldDescription>
                  Texto destacado que aparece como badge
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel>Landbot Config</FieldLabel>
                <Input
                  value={jsonArrayData.landbotConfig}
                  onChange={(e) => handleArrayFieldChange('landbotConfig', e.target.value)}
                  placeholder="ej: C35070"
                />
                <FieldDescription>
                  Configuración del chatbot Landbot
                </FieldDescription>
              </Field>
            </div>
          </FieldGroup>

          <FieldGroup>
            <Field>
              <FieldLabel>Descripción</FieldLabel>
              <Textarea
                value={jsonArrayData.descripcion}
                onChange={(e) => handleArrayFieldChange('descripcion', e.target.value)}
                placeholder="Descripción del plan"
                rows={3}
              />
              <FieldDescription>
                Descripción detallada del plan de seguros
              </FieldDescription>
            </Field>

            <Field>
              <FieldLabel>Nota</FieldLabel>
              <Textarea
                value={jsonArrayData.nota}
                onChange={(e) => handleArrayFieldChange('nota', e.target.value)}
                placeholder="Nota informativa"
                rows={3}
              />
              <FieldDescription>
                Nota adicional o información importante sobre el plan
              </FieldDescription>
            </Field>
          </FieldGroup>

          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium">Alternativa de Comunicación</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field>
                <FieldLabel>Título</FieldLabel>
                <Input
                  value={jsonArrayData.comunicacion?.titulo || ""}
                  onChange={(e) => handleCommunicationChange("titulo", e.target.value)}
                  placeholder="Título de comunicación"
                />
                <FieldDescription>
                  Título del bloque de comunicación para el front.
                </FieldDescription>
              </Field>
            </div>
            <Field>
              <FieldLabel>Contenido</FieldLabel>
              <Textarea
                value={jsonArrayData.comunicacion?.contenido || ""}
                onChange={(e) => handleCommunicationChange("contenido", e.target.value)}
                placeholder="Contenido principal de comunicación"
                rows={4}
              />
              <FieldDescription>
                Texto base de la comunicación que acompañará los destacados.
              </FieldDescription>
            </Field>
            <div className="rounded-lg border p-4 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label>Palabras a destacar</Label>
                  <p className="text-sm text-muted-foreground">
                    Se guardan en jsonarray con el nombre del color Fancy Yool.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-3 items-end">
                <div>
                  <Label>Palabra o frase</Label>
                  <Input
                    value={newHighlightedWord.texto}
                    onChange={(e) => setNewHighlightedWord((prev) => ({ ...prev, texto: e.target.value }))}
                    placeholder="ej: cobertura premium"
                  />
                </div>
                <div>
                  <Label>Color Fancy</Label>
                  <Select
                    value={newHighlightedWord.color}
                    onValueChange={(value) => setNewHighlightedWord((prev) => ({ ...prev, color: value }))}
                  >
                    <SelectTrigger style={{ backgroundColor: colors.light.surface.default }}>
                      <SelectValue placeholder="Color" />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: colors.light.surface.default }}>
                      {fancyColorOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAddHighlightedWord}>
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar
                </Button>
              </div>
              <div className="space-y-3">
                {(jsonArrayData.comunicacion?.palabrasDestacadas || []).map((item, index) => (
                  <div key={`${item.texto}-${index}`} className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto_auto] gap-3 items-center">
                    <Input
                      value={item.texto || ""}
                      onChange={(e) => handleHighlightedWordChange(index, "texto", e.target.value)}
                      placeholder="Palabra o frase"
                    />
                    <Select
                      value={item.color || "teal"}
                      onValueChange={(value) => handleHighlightedWordChange(index, "color", value)}
                    >
                      <SelectTrigger style={{ backgroundColor: colors.light.surface.default }}>
                        <SelectValue placeholder="Color" />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: colors.light.surface.default }}>
                        {fancyColorOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm text-center">
                      <span className={getFancyHighlightClass(item.color)}>
                        {item.texto || "Vista previa"}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => handleRemoveHighlightedWord(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {!(jsonArrayData.comunicacion?.palabrasDestacadas || []).length && (
                  <div className="text-sm text-muted-foreground">
                    Aún no hay palabras destacadas configuradas.
                  </div>
                )}
              </div>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Vista previa del bloque</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-xl border bg-slate-50 p-5 space-y-4">
                  <div className="space-y-2">
                    <h5 className="text-xl font-semibold leading-tight text-slate-900">
                      {renderHighlightedText(
                        jsonArrayData.comunicacion?.titulo || "Título de comunicación",
                        communicationHighlightedWords
                      )}
                    </h5>
                    <p className="whitespace-pre-wrap text-sm leading-6 text-slate-600">
                      {renderHighlightedText(
                        jsonArrayData.comunicacion?.contenido || "Contenido principal de la comunicación.",
                        communicationHighlightedWords
                      )}
                    </p>
                  </div>
                  {!!communicationHighlightedWords.length && (
                    <div className="flex flex-wrap gap-3">
                      {communicationHighlightedWords.map((item, index) => (
                        <span
                          key={`${item.texto}-${item.color}-${index}`}
                          className={`rounded-full border bg-white px-3 py-1 text-sm ${getFancyHighlightClass(item.color)}`}
                        >
                          {item.texto}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Agregar nuevo item */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Agregar Nuevo Item</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
              <div>
                <Label>Tipo</Label>
                <Select value={newItem.tipo} onValueChange={(value) => setNewItem(prev => ({ ...prev, tipo: value }))}>
                  <SelectTrigger style={{ backgroundColor: colors.light.surface.default }}>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: colors.light.surface.default }}>
                    {tiposDisponibles.map(tipo => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Título</Label>
                <Input
                  value={newItem.titulo}
                  onChange={(e) => setNewItem(prev => ({ ...prev, titulo: e.target.value }))}
                  placeholder="Título del item"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleAddItem} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar
                </Button>
              </div>
            </div>
            <div>
              <Label>Contenido</Label>
              <Textarea
                value={newItem.item}
                onChange={(e) => setNewItem(prev => ({ ...prev, item: e.target.value }))}
                placeholder="Contenido del item"
                rows={3}
              />
            </div>
          </div>

          {/* Items agrupados por tipo */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Items por Tipo</h4>
            {Object.entries(groupedItems).map(([tipo, items]) => (
              <div key={tipo} className="mb-6">
                <h5 className="font-medium text-sm text-gray-700 mb-2 capitalize">{tipo}</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((item, index) => (
                    <div 
                      key={item.originalIndex} 
                      className="group"
                      style={{
                        backgroundColor: cardTokens?.variants?.interactive?.backgroundColor || '#ffffff',
                        border: cardTokens?.variants?.interactive?.border || '1px solid #e5e7eb',
                        borderRadius: borders?.radius?.lg || '8px',
                        boxShadow: cardTokens?.variants?.interactive?.boxShadow || '0 1px 3px rgba(0, 0, 0, 0.1)',
                        padding: cardTokens?.variants?.interactive?.padding || '16px',
                        cursor: cardTokens?.variants?.interactive?.cursor || 'pointer',
                        transition: cardTokens?.variants?.interactive?.transition || 'all 0.2s ease-in-out',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = cardTokens.variants.interactive['&:hover'].boxShadow;
                        e.currentTarget.style.transform = cardTokens.variants.interactive['&:hover'].transform;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = cardTokens.variants.interactive.boxShadow;
                        e.currentTarget.style.transform = 'none';
                      }}
                    >
                      {editingItem === item.originalIndex ? (
                        <EditItemForm
                          item={item}
                          onSave={(updatedItem) => handleSaveItem(item.originalIndex, updatedItem)}
                          onCancel={() => setEditingItem(null)}
                          tiposDisponibles={tiposDisponibles}
                        />
                      ) : (
                        <div className="flex flex-col h-full">
                          {/* Header con icono y tipo */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                                style={{ backgroundColor: colors.light.primary.main }}
                              >
                                {tipo.charAt(0).toUpperCase()}
                              </div>
                              <span 
                                className="text-xs font-medium uppercase tracking-wide"
                                style={{ color: colors.light.text.secondary }}
                              >
                                {tipo}
                              </span>
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditItem(item.originalIndex)}
                                style={{
                                  padding: buttonTokens?.sizes?.xs?.padding || '4px 8px',
                                  fontSize: buttonTokens?.sizes?.xs?.fontSize || '12px',
                                  borderRadius: borders?.radius?.md || '6px',
                                  backgroundColor: colors?.light?.danger?.[600] || '#dc2626',
                                  borderColor: colors?.light?.danger?.[600] || '#dc2626',
                                  color: 'white',
                                }}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteItem(item.originalIndex)}
                                style={{
                                  padding: buttonTokens?.sizes?.xs?.padding || '4px 8px',
                                  fontSize: buttonTokens?.sizes?.xs?.fontSize || '12px',
                                  borderRadius: borders?.radius?.md || '6px',
                                  backgroundColor: colors?.light?.danger?.[600] || '#dc2626',
                                  borderColor: colors?.light?.danger?.[600] || '#dc2626',
                                  color: 'white',
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Contenido de la tarjeta */}
                          <div className="flex-1">
                            <h6 
                              className="font-semibold mb-2 line-clamp-2"
                              style={{ 
                                color: colors.light.text.primary,
                                fontSize: typography.fontSizes.sm,
                                lineHeight: typography.lineHeights.tight
                              }}
                            >
                              {item.titulo}
                            </h6>
                            <p 
                              className="text-sm line-clamp-3"
                              style={{ 
                                color: colors.light.text.secondary,
                                lineHeight: typography.lineHeights.relaxed
                              }}
                            >
                              {item.item}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sección JSONMails */}
      <Card>
        <CardHeader>
          <CardTitle>Configuración JSONMails</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Cotización */}
          <Card className="shadow-lg border-0 bg-white hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-3 text-xl font-semibold text-gray-900">
                <Mail className="h-6 w-6" style={{ color: colors.light.secondary[600] }} />
                Email Cotización/Simulación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
            <div className="space-y-4">
              {/* Header */}
              <div>
                <Label className="text-sm font-medium">Header</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <Input
                    placeholder="Título"
                    value={jsonMailsData.email_cotizacion?.header?.titulo || ""}
                    onChange={(e) => handleNestedMailFieldChange('email_cotizacion', 'header', 'titulo', e.target.value)}
                  />
                  <Input
                    placeholder="Subtítulo"
                    value={jsonMailsData.email_cotizacion?.header?.subtitulo || ""}
                    onChange={(e) => handleNestedMailFieldChange('email_cotizacion', 'header', 'subtitulo', e.target.value)}
                  />
                </div>
              </div>

              {/* Saludo */}
              <div>
                <Label className="text-sm font-medium">Saludo</Label>
                <Textarea
                  placeholder="Texto de saludo"
                  value={jsonMailsData.email_cotizacion?.saludo?.texto || ""}
                  onChange={(e) => handleNestedMailFieldChange('email_cotizacion', 'saludo', 'texto', e.target.value)}
                  rows={2}
                  className="mt-2"
                />
              </div>

              {/* CTA */}
              <div>
                <Label className="text-sm font-medium">Call to Action</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <Input
                    placeholder="URL"
                    value={jsonMailsData.email_cotizacion?.cta?.url || ""}
                    onChange={(e) => handleNestedMailFieldChange('email_cotizacion', 'cta', 'url', e.target.value)}
                  />
                  <Input
                    placeholder="Texto del botón"
                    value={jsonMailsData.email_cotizacion?.cta?.texto || ""}
                    onChange={(e) => handleNestedMailFieldChange('email_cotizacion', 'cta', 'texto', e.target.value)}
                  />
                </div>
              </div>

              {/* Beneficios */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <Label 
                    className="text-sm font-medium"
                    style={{ color: colors.light.text.primary }}
                  >
                    Beneficios
                  </Label>
                  <Button 
                    size="sm" 
                    onClick={() => handleAddBeneficio('email_cotizacion')}
                    style={{
                      backgroundColor: colors?.light?.secondary?.[600] || '#6b7280',
                      color: 'white',
                      padding: buttonTokens?.sizes?.sm?.padding || '8px 12px',
                      fontSize: buttonTokens?.sizes?.sm?.fontSize || '14px',
                      borderRadius: borders?.radius?.md || '6px',
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" style={{ color: 'white' }} />
                    Agregar Beneficio
                  </Button>
                </div>
                <Input
                  placeholder="Título de beneficios"
                  value={jsonMailsData.email_cotizacion?.beneficios?.titulo || ""}
                  onChange={(e) => handleNestedMailFieldChange('email_cotizacion', 'beneficios', 'titulo', e.target.value)}
                  className="mb-4"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(jsonMailsData.email_cotizacion.beneficios.items || []).map((beneficio, index) => (
                    <BeneficioForm
                      key={index}
                      beneficio={beneficio}
                      index={index}
                      iconosDisponibles={iconos_disponibles}
                      onChange={(updatedBeneficio) => {
                        const updatedData = {
                          ...jsonMailsData,
                          email_cotizacion: {
                            ...jsonMailsData.email_cotizacion,
                            beneficios: {
                              ...jsonMailsData.email_cotizacion.beneficios,
                              items: (jsonMailsData.email_cotizacion.beneficios.items || []).map((item, i) => 
                                i === index ? updatedBeneficio : item
                              )
                            }
                          }
                        };
                        setJsonMailsData(updatedData);
                        handleChange({
                          target: {
                            name: 'jsonmails',
                            value: JSON.stringify(updatedData)
                          }
                        });
                      }}
                      onDelete={() => {
                        const updatedData = {
                          ...jsonMailsData,
                          email_cotizacion: {
                            ...jsonMailsData.email_cotizacion,
                            beneficios: {
                              ...jsonMailsData.email_cotizacion.beneficios,
                              items: (jsonMailsData.email_cotizacion.beneficios.items || []).filter((_, i) => i !== index)
                            }
                          }
                        };
                        setJsonMailsData(updatedData);
                        handleChange({
                          target: {
                            name: 'jsonmails',
                            value: JSON.stringify(updatedData)
                          }
                        });
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
            </CardContent>
          </Card>

          {/* Email Contratación */}
          <Card className="shadow-lg border-0 bg-white hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-3 text-xl font-semibold text-gray-900">
                <CheckCircle className="h-6 w-6" style={{ color: colors.light.success[600] }} />
                Email Contratación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
            <div className="space-y-4">
              {/* Header */}
              <div>
                <Label className="text-sm font-medium">Header</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <Input
                    placeholder="Título"
                    value={jsonMailsData.email_contratacion?.header?.titulo || ""}
                    onChange={(e) => handleNestedMailFieldChange('email_contratacion', 'header', 'titulo', e.target.value)}
                  />
                  <Input
                    placeholder="Subtítulo"
                    value={jsonMailsData.email_contratacion?.header?.subtitulo || ""}
                    onChange={(e) => handleNestedMailFieldChange('email_contratacion', 'header', 'subtitulo', e.target.value)}
                  />
                </div>
              </div>

              {/* Saludo */}
              <div>
                <Label className="text-sm font-medium">Saludo</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <Input
                    placeholder="Título del saludo"
                    value={jsonMailsData.email_contratacion?.saludo?.titulo || ""}
                    onChange={(e) => handleNestedMailFieldChange('email_contratacion', 'saludo', 'titulo', e.target.value)}
                  />
                  <Textarea
                    placeholder="Texto del saludo"
                    value={jsonMailsData.email_contratacion?.saludo?.texto || ""}
                    onChange={(e) => handleNestedMailFieldChange('email_contratacion', 'saludo', 'texto', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>

              {/* Info Cards */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <Label 
                    className="text-sm font-medium"
                    style={{ color: colors.light.text.primary }}
                  >
                    Tarjetas Informativas
                  </Label>
                  <Button 
                    size="sm" 
                    onClick={handleAddInfoCard}
                    style={{
                      backgroundColor: colors?.light?.success?.[600] || '#16a34a',
                      color: 'white',
                      padding: buttonTokens?.sizes?.sm?.padding || '8px 12px',
                      fontSize: buttonTokens?.sizes?.sm?.fontSize || '14px',
                      borderRadius: borders?.radius?.md || '6px',
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" style={{ color: 'white' }} />
                    Agregar Tarjeta
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(jsonMailsData.email_contratacion.info_cards || []).map((card, index) => (
                    <InfoCardForm
                      key={index}
                      card={card}
                      index={index}
                      iconosDisponibles={iconos_disponibles}
                      onChange={(updatedCard) => {
                        const updatedData = {
                          ...jsonMailsData,
                          email_contratacion: {
                            ...jsonMailsData.email_contratacion,
                            info_cards: (jsonMailsData.email_contratacion.info_cards || []).map((item, i) => 
                              i === index ? updatedCard : item
                            )
                          }
                        };
                        setJsonMailsData(updatedData);
                        handleChange({
                          target: {
                            name: 'jsonmails',
                            value: JSON.stringify(updatedData)
                          }
                        });
                      }}
                      onDelete={() => {
                        const updatedData = {
                          ...jsonMailsData,
                          email_contratacion: {
                            ...jsonMailsData.email_contratacion,
                            info_cards: (jsonMailsData.email_contratacion.info_cards || []).filter((_, i) => i !== index)
                          }
                        };
                        setJsonMailsData(updatedData);
                        handleChange({
                          target: {
                            name: 'jsonmails',
                            value: JSON.stringify(updatedData)
                          }
                        });
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Botón de Guardar */}
      <div className="mt-6 flex justify-end">
        <Button 
          onClick={handleSave}
          disabled={isSaving}
          style={{
            backgroundColor: isSaving ? (colors?.light?.gray?.[400] || '#9ca3af') : (colors?.light?.primary?.[600] || '#2563eb'),
            color: 'white',
            padding: buttonTokens?.sizes?.default?.padding || '12px 16px',
            fontSize: buttonTokens?.sizes?.default?.fontSize || '16px',
            borderRadius: borders?.radius?.md || '6px',
            minWidth: '120px'
          }}
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Guardando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Guardar Cambios
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// Componente para editar items
function EditItemForm({ item, onSave, onCancel, tiposDisponibles }) {
  const [editData, setEditData] = useState(item);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Tipo</Label>
          <Select value={editData.tipo} onValueChange={(value) => setEditData(prev => ({ ...prev, tipo: value }))}>
            <SelectTrigger style={{ backgroundColor: colors.light.surface.default }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ backgroundColor: colors.light.surface.default }}>
              {tiposDisponibles.map(tipo => (
                <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Título</Label>
          <Input
            value={editData.titulo}
            onChange={(e) => setEditData(prev => ({ ...prev, titulo: e.target.value }))}
          />
        </div>
      </div>
      <div>
        <Label className="text-xs">Contenido</Label>
        <Textarea
          value={editData.item}
          onChange={(e) => setEditData(prev => ({ ...prev, item: e.target.value }))}
          rows={3}
        />
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onSave(editData)}>
          <Save className="h-3 w-3 mr-1" />
          Guardar
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          <X className="h-3 w-3 mr-1" />
          Cancelar
        </Button>
      </div>
    </div>
  );
}

// Componente para beneficios
function BeneficioForm({ beneficio, index, iconosDisponibles, onChange, onDelete }) {
  return (
    <div 
      className="group relative"
      style={{
        backgroundColor: cardTokens.variants.default.backgroundColor,
        border: cardTokens.variants.default.border,
        borderRadius: borders.radius.lg,
        boxShadow: cardTokens.variants.default.boxShadow,
        padding: cardTokens.variants.default.padding,
        transition: 'all 0.2s ease-in-out',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = shadows.component.card.hover;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = cardTokens.variants.default.boxShadow;
        e.currentTarget.style.transform = 'none';
      }}
    >
      {/* Header con icono */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold"
            style={{ backgroundColor: colors.light.secondary[600] }}
          >
            {beneficio.icon && iconComponents[beneficio.icon] ? (
              React.createElement(iconComponents[beneficio.icon], { className: "h-5 w-5", style: { color: 'white' } })
            ) : (
              'B'
            )}
          </div>
          <div>
            <h6 
              className="font-semibold"
              style={{ 
                color: colors.light.text.primary,
                fontSize: typography.fontSizes.sm
              }}
            >
              {beneficio.titulo || 'Nuevo Beneficio'}
            </h6>
            <span 
              className="text-xs"
              style={{ color: colors.light.text.secondary }}
            >
              {iconosDisponibles[beneficio.icon] || 'Seleccionar icono'}
            </span>
          </div>
        </div>
        <Button 
          size="sm" 
          variant="destructive" 
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            padding: buttonTokens.sizes.xs.padding,
            fontSize: buttonTokens.sizes.xs.fontSize,
            borderRadius: borders.radius.md,
          }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {/* Formulario */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label 
              className="text-xs font-medium"
              style={{ color: colors.light.text.secondary }}
            >
              Icono
            </Label>
            <Select value={beneficio.icon} onValueChange={(value) => onChange({ ...beneficio, icon: value })}>
              <SelectTrigger style={{ backgroundColor: colors.light.surface.default }}>
                <div className="flex items-center gap-2">
                  {beneficio.icon && iconComponents[beneficio.icon] && (
                    React.createElement(iconComponents[beneficio.icon], { className: "h-4 w-4" })
                  )}
                  <SelectValue placeholder="Seleccionar icono" />
                </div>
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: colors.light.surface.default }}>
                {Object.entries(iconosDisponibles).map(([key, description]) => {
                  const IconComponent = iconComponents[key];
                  return (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="h-4 w-4" />}
                        <span>{key} - {description}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label 
              className="text-xs font-medium"
              style={{ color: colors.light.text.secondary }}
            >
              Título
            </Label>
            <Input
              value={beneficio.titulo}
              onChange={(e) => onChange({ ...beneficio, titulo: e.target.value })}
              placeholder="Título del beneficio"
            />
          </div>
        </div>
        <div>
          <Label 
            className="text-xs font-medium"
            style={{ color: colors.light.text.secondary }}
          >
            Contenido
          </Label>
          <Textarea
            value={beneficio.content}
            onChange={(e) => onChange({ ...beneficio, content: e.target.value })}
            placeholder="Descripción del beneficio"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
}

// Componente para info cards
function InfoCardForm({ card, index, iconosDisponibles, onChange, onDelete }) {
  return (
    <div 
      className="group relative"
      style={{
        backgroundColor: cardTokens.variants.default.backgroundColor,
        border: cardTokens.variants.default.border,
        borderRadius: borders.radius.lg,
        boxShadow: cardTokens.variants.default.boxShadow,
        padding: cardTokens.variants.default.padding,
        transition: 'all 0.2s ease-in-out',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = shadows.component.card.hover;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = cardTokens.variants.default.boxShadow;
        e.currentTarget.style.transform = 'none';
      }}
    >
      {/* Header con icono */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold"
            style={{ backgroundColor: colors.light.success[600] }}
          >
            {card.icon && iconComponents[card.icon] ? (
              React.createElement(iconComponents[card.icon], { className: "h-5 w-5", style: { color: 'white' } })
            ) : (
              'I'
            )}
          </div>
          <div>
            <h6 
              className="font-semibold"
              style={{ 
                color: colors.light.text.primary,
                fontSize: typography.fontSizes.sm
              }}
            >
              {card.titulo || 'Nueva Tarjeta'}
            </h6>
            <span 
              className="text-xs"
              style={{ color: colors.light.text.secondary }}
            >
              {iconosDisponibles[card.icon] || 'Seleccionar icono'}
            </span>
          </div>
        </div>
        <Button 
          size="sm" 
          variant="destructive" 
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            padding: buttonTokens.sizes.xs.padding,
            fontSize: buttonTokens.sizes.xs.fontSize,
            borderRadius: borders.radius.md,
          }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {/* Formulario */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label 
              className="text-xs font-medium"
              style={{ color: colors.light.text.secondary }}
            >
              Icono
            </Label>
            <Select value={card.icon} onValueChange={(value) => onChange({ ...card, icon: value })}>
              <SelectTrigger style={{ backgroundColor: colors.light.surface.default }}>
                <div className="flex items-center gap-2">
                  {card.icon && iconComponents[card.icon] && (
                    React.createElement(iconComponents[card.icon], { className: "h-4 w-4" })
                  )}
                  <SelectValue placeholder="Seleccionar icono" />
                </div>
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: colors.light.surface.default }}>
                {Object.entries(iconosDisponibles).map(([key, description]) => {
                  const IconComponent = iconComponents[key];
                  return (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="h-4 w-4" />}
                        <span>{key} - {description}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label 
              className="text-xs font-medium"
              style={{ color: colors.light.text.secondary }}
            >
              Título
            </Label>
            <Input
              value={card.titulo}
              onChange={(e) => onChange({ ...card, titulo: e.target.value })}
              placeholder="Título de la tarjeta"
            />
          </div>
        </div>
        <div>
          <Label 
            className="text-xs font-medium"
            style={{ color: colors.light.text.secondary }}
          >
            Contenido
          </Label>
          <Textarea
            value={card.content}
            onChange={(e) => onChange({ ...card, content: e.target.value })}
            placeholder="Contenido de la tarjeta informativa"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
}
